import PropTypes from "prop-types";

/**
 * Determines the conditions under which a tree node can be dropped in the tree. These conditions represent where in the tree view the node can be dropped.
 * @param {Array<Object>} currentTree - The current tree structure. Each tree node is an object. See {@link https://github.com/minop1205/react-dnd-treeview#node-properties node properties} in the React DnD TreeView documentation.
 * @param {Object} options - The canDrop callback options. See the {@link https://github.com/minop1205/react-dnd-treeview#canDrop-callback canDrop callback} in the React DnD TreeView documentation.
 * @param {Object} dropConfig - Custom configuration options for dropping nodes. See the PropTypes for all available dropConfig options.
 * @returns {Array<boolean>} An array of conditions under which a node can be dropped and where in the tree it can be dropped.
 */
export function getDropConditions(currentTree, options, dropConfig) {
  const { dragSource, dropTarget } = options;
  const dropConditions = [];

  // Checks if the attempted drop changes the dragged node's order of appearance with respect to its siblings
  const checkSiblingReorder = () =>
    dragSource?.parent === dropTarget?.id ||
    (dragSource?.parent === null && dropTarget === undefined); // Allows reordering for nodes of level 0

  // Checks if the attempted drop changes the dragged node's parent while keeping it at the same level
  const checkMoveToSameLevel = () =>
    dropTarget?.data.depth === dragSource?.data.depth - 1 &&
    dragSource?.parent !== dropTarget?.id;

  // Checks if the attempted drop changes the dragged node's parent while moving it to a higher level
  const checkMoveToUpperLevel = () =>
    dropTarget?.data.depth < dragSource?.data.depth - 1 ||
    (dragSource?.data.depth > 0 && dropTarget === undefined); // Allows any node to be moved to level 0
  // Add more check functions as needed

  // Checks if the dragged node is being dropped on a sibling
  const checkDropOnSibling = () =>
    dragSource?.parent === dropTarget?.parent &&
    dragSource?.id !== dropTarget.id; // Does not allow any node to be dropped on self

  // Mapping between drop options and their corresponding conditions
  const dropOptionMappings = [
    {
      option: "allowSiblingReorder",
      condition: checkSiblingReorder(),
    },
    {
      option: "allowMoveToSameLevel",
      condition: checkMoveToSameLevel(),
    },
    {
      option: "allowMoveToUpperLevel",
      condition: checkMoveToUpperLevel(),
    },
    {
      option: "allowDropOnSibling",
      condition: checkDropOnSibling(),
    },
    // Add more mappings as needed
  ];

  // Iterate over each mapping in dropOptionMappings array
  dropOptionMappings.forEach((mapping) => {
    // Check if the option exists in the dropConfig object
    if (dropConfig[mapping.option]) {
      // If the option exists, push the mapping's condition to the dropConditions array
      dropConditions.push(mapping.condition);
    }
  });

  return dropConditions;
}

getDropConditions.propTypes = {
  currentTree: PropTypes.array.isRequired,
  options: PropTypes.object.isRequired,
  dropConfig: PropTypes.shape({
    allowSiblingReorder: PropTypes.bool,
    allowMoveToSameLevel: PropTypes.bool,
    allowMoveToUpperLevel: PropTypes.bool,
    allowDropOnSibling: PropTypes.bool,
    // Add more dropConfig options as needed
  }).isRequired,
};
